import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import { dollarToleranceCents } from '@ovComponents/4-module/widgets/portfolio/components/allocationTolerances';
import { chooseCellsToRoundUp, type RoundingCell } from './controlledRounding';

// Balances in float cents, then rounds once so every row and column hits its
// whole-cent target. Save-time wire fractions come from
// `canonicalizeWireMatrixFractions`, not from here.

export type LockedMatrixCell = { sa: string; fp: string; amountCents: number };

export type LockedMatrixMember = { sa: string; mvCents: number };

export type LockedMatrixInput = {
  members: LockedMatrixMember[];
  cells: LockedMatrixCell[];
  rowTargetFraction: Map<string, number>; // fp -> R[fp]
  lockedKeys: Set<string>; // allocationCellKey(sa, fp)
  securityMassFraction: number; // 1 - expectedCashPercentage
  maxIterations?: number;
};

export const SolveStatuses = {
  OK: 'ok',
  INFEASIBLE: 'infeasible',
  FAILED: 'failed',
} as const;

export type SolveStatus = (typeof SolveStatuses)[keyof typeof SolveStatuses];

export type LockedMatrixOutput = {
  status: SolveStatus;
  after: LockedMatrixCell[];
};

export const MAX_SOLVER_ITERATIONS = 1000;
export const SOLVER_CENTS_EPS = 1;

const FLOAT_CONVERGENCE_CENTS = 0.01;
const STALL_ITERATION_LIMIT = 25;
const STALL_MIN_IMPROVEMENT_CENTS = 1e-9;

type Marginal = {
  targetCents: number;
  lockedCents: number;
  freeIndexes: number[];
};

type Marginals = {
  marginals: Marginal[];
  marginalOfCell: number[];
};

export const buildHouseholdRowTargetCents = (
  rowTargetFraction: Map<string, number>,
  mvTotalCents: number,
  securityMassFraction: number,
): Map<string, number> => {
  const rowFractionSum = [...rowTargetFraction.values()].reduce(
    (sum, fraction) => sum + fraction,
    0,
  );
  const normalizedDenominator = rowFractionSum > 0 ? rowFractionSum : 1;
  const investableMassCents = mvTotalCents * securityMassFraction;
  const targetTotalCents = Math.round(investableMassCents);

  // Last row absorbs the rounding remainder so Σ targetRowCents matches
  // targetTotalCents exactly — independent per-row rounding can otherwise
  // drift a few cents, breaking downstream marginal conservation.
  const fps = [...rowTargetFraction.keys()];
  const targetRowCents = new Map<string, number>();
  let runningCents = 0;
  fps.forEach((fp, index) => {
    const fraction = rowTargetFraction.get(fp) ?? 0;
    const isLast = index === fps.length - 1;
    const cents = isLast
      ? targetTotalCents - runningCents
      : Math.round((fraction / normalizedDenominator) * investableMassCents);
    targetRowCents.set(fp, cents);
    runningCents += cents;
  });
  return targetRowCents;
};

// Largest-remainder split: per-member rounding can leave Σ column targets a
// cent off Σ row targets, which no matrix can satisfy.
export const buildMemberColumnTargetCents = (
  members: LockedMatrixMember[],
  securityMassFraction: number,
  totalTargetCents: number,
  minimumCentsByMember = new Map<string, number>(),
): Map<string, number> => {
  const shares = members.map((member, index) => {
    const exactCents = member.mvCents * securityMassFraction;
    const floorCents = Math.floor(exactCents);
    return {
      member,
      index,
      floorCents,
      remainder: exactCents - floorCents,
    };
  });
  const floorSum = shares.reduce((sum, share) => sum + share.floorCents, 0);
  const extraCents = Math.min(
    Math.max(totalTargetCents - floorSum, 0),
    members.length,
  );
  const byRemainderDesc = [...shares].sort((a, b) => b.remainder - a.remainder);

  const roundUpIndexes = new Set<number>();
  for (const { floorCents, index, member } of byRemainderDesc) {
    if (
      (minimumCentsByMember.get(member.sa) ?? 0) > floorCents &&
      roundUpIndexes.size < extraCents
    ) {
      roundUpIndexes.add(index);
    }
  }
  for (const { index } of byRemainderDesc) {
    if (roundUpIndexes.size >= extraCents) break;
    roundUpIndexes.add(index);
  }

  const targets = new Map<string, number>();
  shares.forEach(({ floorCents, index, member }) => {
    targets.set(member.sa, floorCents + (roundUpIndexes.has(index) ? 1 : 0));
  });
  return targets;
};

const buildMarginals = (
  cells: LockedMatrixCell[],
  isLocked: boolean[],
  targetCentsByKey: Map<string, number>,
  keyOf: (cell: LockedMatrixCell) => string,
) => {
  const cellIndexesByKey = new Map<string, number[]>();
  cells.forEach((cell, index) => {
    const key = keyOf(cell);
    const indexes = cellIndexesByKey.get(key);
    if (indexes) {
      indexes.push(index);
    } else {
      cellIndexesByKey.set(key, [index]);
    }
  });

  // A key with cells but no target is a fund outside the model: its target is 0.
  const keys = [
    ...new Set([...targetCentsByKey.keys(), ...cellIndexesByKey.keys()]),
  ];
  const marginalOfCell: number[] = new Array(cells.length).fill(-1);
  const marginals = keys.map((key, marginalIndex): Marginal => {
    const indexes = cellIndexesByKey.get(key) ?? [];
    let lockedCents = 0;
    const freeIndexes: number[] = [];
    for (const index of indexes) {
      marginalOfCell[index] = marginalIndex;
      if (isLocked[index]) {
        lockedCents += cells[index]!.amountCents;
      } else {
        freeIndexes.push(index);
      }
    }
    return {
      targetCents: targetCentsByKey.get(key) ?? 0,
      lockedCents,
      freeIndexes,
    };
  });
  return { marginals, marginalOfCell };
};

const hasFreeCells = (marginal: Marginal) => marginal.freeIndexes.length > 0;

// Fully locked marginals are judged by `isPinnedOffTarget` instead, so a gap
// of the same size is accepted on both sides of the target.
const isLockedBeyondTarget = (marginal: Marginal) =>
  hasFreeCells(marginal) && marginal.lockedCents > marginal.targetCents;

// With no free cell the marginal cannot move, so a gap beyond the tolerance is
// unfixable.
const isPinnedOffTarget = (marginal: Marginal, toleranceCents: number) =>
  !hasFreeCells(marginal) &&
  Math.abs(marginal.targetCents - marginal.lockedCents) > toleranceCents;

const roomCents = (marginal: Marginal) =>
  marginal.targetCents - marginal.lockedCents;

const sumCents = (values: number[]) =>
  values.reduce((sum, value) => sum + value, 0);

const splitCentsProportionally = (totalCents: number, weights: number[]) => {
  const weightSum = sumCents(weights);
  const exactShares = weights.map(
    (weight) => (totalCents * weight) / weightSum,
  );
  const shares = exactShares.map(Math.floor);
  const byRemainderDesc = exactShares
    .map((exact, index) => ({ index, remainder: exact - shares[index]! }))
    .sort((a, b) => b.remainder - a.remainder);
  const leftoverCents = totalCents - sumCents(shares);
  byRemainderDesc.slice(0, leftoverCents).forEach(({ index }) => {
    shares[index]! += 1;
  });
  return shares;
};

// The room left in the crossing marginals bounds what this marginal can take.
const crossingRoomCents = (
  marginal: Marginal,
  crossing: Marginal[],
  crossingOfCell: number[],
) =>
  sumCents(
    marginal.freeIndexes.map((index) =>
      roomCents(crossing[crossingOfCell[index]!]!),
    ),
  );

const hasInsufficientCrossingRoom = (
  side: Marginal[],
  crossing: Marginal[],
  crossingOfCell: number[],
) =>
  side.some(
    (marginal) =>
      hasFreeCells(marginal) &&
      roomCents(marginal) >
        crossingRoomCents(marginal, crossing, crossingOfCell) +
          SOLVER_CENTS_EPS,
  );

// Cents a fund can still take on top of its own room before the member columns
// it crosses run out.
const headroomCents = (marginal: Marginal, cols: Marginals) =>
  Math.max(
    crossingRoomCents(marginal, cols.marginals, cols.marginalOfCell) -
      roomCents(marginal),
    0,
  );

// Spread in proportion to what each fund still needs, unless a fund cannot take
// its share (funds held by only some members): then follow the headroom.
const splitExtraCents = (
  extraCents: number,
  needs: number[],
  headrooms: number[],
) => {
  if (sumCents(needs) > 0) {
    const byNeed = splitCentsProportionally(extraCents, needs);
    if (byNeed.every((share, index) => share <= headrooms[index]!)) {
      return byNeed;
    }
  }
  if (sumCents(headrooms) === 0) return undefined;
  return splitCentsProportionally(extraCents, headrooms);
};

// A free fund cannot give back more than it still needs to receive.
const splitGivenBackCents = (givenBackCents: number, needs: number[]) => {
  const needSum = sumCents(needs);
  if (needSum === 0) return undefined;
  return splitCentsProportionally(Math.min(givenBackCents, needSum), needs);
};

// Member columns are the hard constraint (each account must sum to its own
// target), so a fully locked fund that sits off target within tolerance has to
// be made up by the free funds. Otherwise the compromise lands on the columns.
const absorbPinnedGapIntoFreeRows = (rows: Marginals, cols: Marginals) => {
  const freeRows = rows.marginals.filter(hasFreeCells);
  const reachableColumnCents = sumCents(
    cols.marginals.map((marginal) =>
      hasFreeCells(marginal) ? marginal.targetCents : marginal.lockedCents,
    ),
  );
  const pinnedRowCents = sumCents(
    rows.marginals.map((marginal) =>
      hasFreeCells(marginal) ? 0 : marginal.lockedCents,
    ),
  );
  const shiftCents =
    reachableColumnCents -
    pinnedRowCents -
    sumCents(freeRows.map((marginal) => marginal.targetCents));
  if (shiftCents === 0) return rows;

  // Row targets that don't add up to the column targets for any other reason
  // (e.g. no model funds) are not a gap to make up.
  const pinnedGapCents = sumCents(
    [...rows.marginals, ...cols.marginals].map((marginal) =>
      hasFreeCells(marginal)
        ? 0
        : Math.abs(marginal.targetCents - marginal.lockedCents),
    ),
  );
  if (Math.abs(shiftCents) > pinnedGapCents) return rows;

  const needs = freeRows.map((marginal) => Math.max(roomCents(marginal), 0));
  const shares =
    shiftCents > 0
      ? splitExtraCents(
          shiftCents,
          needs,
          freeRows.map((marginal) => headroomCents(marginal, cols)),
        )
      : splitGivenBackCents(-shiftCents, needs);
  if (!shares) return rows;
  const direction = Math.sign(shiftCents);

  let freeRowPosition = 0;
  const marginals = rows.marginals.map((marginal) => {
    if (!hasFreeCells(marginal)) return marginal;
    const share = shares[freeRowPosition++]!;
    return {
      ...marginal,
      targetCents: marginal.targetCents + direction * share,
    };
  });
  return { marginals, marginalOfCell: rows.marginalOfCell };
};

type MarginalTolerances = { rowCents: number; columnCents: number };

const hasUnfixableMarginal = (marginals: Marginal[], toleranceCents: number) =>
  marginals.some(
    (marginal) =>
      isLockedBeyondTarget(marginal) ||
      isPinnedOffTarget(marginal, toleranceCents),
  );

const isStructurallyInfeasible = (
  rows: Marginals,
  cols: Marginals,
  tolerances: MarginalTolerances,
) => {
  return (
    hasUnfixableMarginal(rows.marginals, tolerances.rowCents) ||
    hasUnfixableMarginal(cols.marginals, tolerances.columnCents) ||
    hasInsufficientCrossingRoom(
      rows.marginals,
      cols.marginals,
      cols.marginalOfCell,
    ) ||
    hasInsufficientCrossingRoom(
      cols.marginals,
      rows.marginals,
      rows.marginalOfCell,
    )
  );
};

const sumAt = (values: number[], indexes: number[]) =>
  indexes.reduce((sum, index) => sum + values[index]!, 0);

const scaleFreeCells = (values: number[], marginal: Marginal) => {
  const { freeIndexes } = marginal;
  if (freeIndexes.length === 0) return;

  const need = Math.max(marginal.targetCents - marginal.lockedCents, 0);
  const freeSum = sumAt(values, freeIndexes);

  if (freeSum > 0) {
    const factor = need / freeSum;
    for (const index of freeIndexes) {
      values[index] = values[index]! * factor;
    }
    return;
  }

  const perCell = need / freeIndexes.length;
  for (const index of freeIndexes) {
    values[index] = perCell;
  }
};

const worstGapCents = (values: number[], marginals: Marginal[]) =>
  marginals.reduce((worst, marginal) => {
    if (marginal.freeIndexes.length === 0) return worst;
    const total = marginal.lockedCents + sumAt(values, marginal.freeIndexes);
    return Math.max(worst, Math.abs(total - marginal.targetCents));
  }, 0);

const balanceInFloat = (
  values: number[],
  rows: Marginal[],
  cols: Marginal[],
  maxIterations: number,
) => {
  let bestGap = Number.POSITIVE_INFINITY;
  let gap = Number.POSITIVE_INFINITY;
  let stalledIterations = 0;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    for (const row of rows) scaleFreeCells(values, row);
    for (const col of cols) scaleFreeCells(values, col);

    gap = Math.max(worstGapCents(values, rows), worstGapCents(values, cols));
    if (gap < FLOAT_CONVERGENCE_CENTS) break;

    if (gap > bestGap - STALL_MIN_IMPROVEMENT_CENTS) {
      stalledIterations += 1;
      if (stalledIterations >= STALL_ITERATION_LIMIT) break;
    } else {
      bestGap = gap;
      stalledIterations = 0;
    }
  }
  return gap;
};

const deficitCents = (marginal: Marginal, floors: Map<number, number>) => {
  const floorSum = marginal.freeIndexes.reduce(
    (sum, index) => sum + floors.get(index)!,
    0,
  );
  const deficit = marginal.targetCents - marginal.lockedCents - floorSum;
  return Math.min(Math.max(deficit, 0), marginal.freeIndexes.length);
};

const roundToWholeCents = (
  values: number[],
  isLocked: boolean[],
  rows: Marginals,
  cols: Marginals,
) => {
  const floors = new Map<number, number>();
  const roundingCells: RoundingCell[] = [];
  const roundingIndexes: number[] = [];

  values.forEach((_, index) => {
    if (isLocked[index]) return;
    const floor = Math.floor(values[index]!);
    floors.set(index, floor);
    roundingIndexes.push(index);
    roundingCells.push({
      row: rows.marginalOfCell[index]!,
      col: cols.marginalOfCell[index]!,
      fraction: values[index]! - floor,
    });
  });

  const roundUp = chooseCellsToRoundUp(
    roundingCells,
    rows.marginals.map((marginal) => deficitCents(marginal, floors)),
    cols.marginals.map((marginal) => deficitCents(marginal, floors)),
  );

  // Locked cells are untouched by balancing, so they are already whole cents.
  const rounded = [...values];
  roundingIndexes.forEach((cellIndex, position) => {
    rounded[cellIndex] = floors.get(cellIndex)! + (roundUp[position] ? 1 : 0);
  });
  return rounded;
};

const toCells = (cells: LockedMatrixCell[], amounts: number[]) =>
  cells.map((cell, index) => ({
    sa: cell.sa,
    fp: cell.fp,
    amountCents: amounts[index]!,
  }));

export const lockedMatrixRedistribute = (
  input: LockedMatrixInput,
): LockedMatrixOutput => {
  const {
    members,
    cells,
    rowTargetFraction,
    lockedKeys,
    securityMassFraction,
    maxIterations = MAX_SOLVER_ITERATIONS,
  } = input;

  const mvTotalCents = members.reduce((sum, m) => sum + m.mvCents, 0);
  const toleranceCents = dollarToleranceCents(members.length);
  const isLocked = cells.map((cell) =>
    lockedKeys.has(allocationCellKey(cell.sa, cell.fp)),
  );
  const lockedCentsByMember = new Map<string, number>();
  cells.forEach((cell, index) => {
    if (!isLocked[index]) return;
    lockedCentsByMember.set(
      cell.sa,
      (lockedCentsByMember.get(cell.sa) ?? 0) + cell.amountCents,
    );
  });

  const rowTargetCents = buildHouseholdRowTargetCents(
    rowTargetFraction,
    mvTotalCents,
    securityMassFraction,
  );
  const colTargetCents = buildMemberColumnTargetCents(
    members,
    securityMassFraction,
    Math.round(mvTotalCents * securityMassFraction),
    lockedCentsByMember,
  );

  const cols = buildMarginals(cells, isLocked, colTargetCents, (c) => c.sa);
  const rows = absorbPinnedGapIntoFreeRows(
    buildMarginals(cells, isLocked, rowTargetCents, (c) => c.fp),
    cols,
  );

  const tolerances = {
    rowCents: toleranceCents,
    columnCents: SOLVER_CENTS_EPS,
  };
  if (isStructurallyInfeasible(rows, cols, tolerances)) {
    return { status: SolveStatuses.INFEASIBLE, after: [] };
  }

  const values = cells.map((cell) => cell.amountCents);
  const gapCents = balanceInFloat(
    values,
    rows.marginals,
    cols.marginals,
    maxIterations,
  );

  if (!(gapCents <= SOLVER_CENTS_EPS)) {
    return {
      status: SolveStatuses.FAILED,
      after: toCells(
        cells,
        values.map((value) => Math.round(value)),
      ),
    };
  }

  const rounded = roundToWholeCents(values, isLocked, rows, cols);
  const isWithinTargets =
    worstGapCents(rounded, rows.marginals) <= tolerances.rowCents &&
    worstGapCents(rounded, cols.marginals) <= tolerances.columnCents;

  return {
    status: isWithinTargets ? SolveStatuses.OK : SolveStatuses.FAILED,
    after: toCells(cells, rounded),
  };
};
